import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useAddDomainToNodeAttribute,
  useCreateNodeAttribute,
  useGetNodeAttribute,
  useRemoveDomainFromNodeAttribute,
  useUpdateNodeAttribute,
} from '../../hooks/api/use-ontology.hook';
import { AttributeApiModel, AttributeRelationship, WithDomains } from '../../../../types/models/attribute.model';
import { LoadingIndicator } from '../LoadingIndicator';
import { AttributeForm } from './AttributeForm';
import { useSearchDomains } from '../../hooks/api/use-domains.hook';
import { AttributeEntryType } from '../../../../types/constants/attribute-entry-type';
import { AttributeType } from '../../../../types/constants/attribute-type';
import { getDirtyValues } from '../../utils/react-hook-form';

export const AddEditAttribute = () => {
  const navigate = useNavigate();
  const { nodeId, attributeId } = useParams<{ nodeId: string; attributeId: string }>();

  const {
    attribute,
    loading: loadingAttribute,
    error: gettingError,
  } = useGetNodeAttribute<AttributeApiModel & WithDomains>({
    nodeId: Number(nodeId),
    attributeId: Number(attributeId),
    include: [AttributeRelationship.DOMAINS],
  });
  const [createAttribute, { loading: creatingAttribute, error: createError }] = useCreateNodeAttribute();
  const [updateAttribute, { loading: updatingAttribute, error: updateError }] = useUpdateNodeAttribute();
  const [assignDomainToAttribute] = useAddDomainToNodeAttribute();
  const [removeDomainFromAttribute] = useRemoveDomainFromNodeAttribute();
  const { loading: loadingDomains, response: domainsResponse, error: domainsError } = useSearchDomains({});

  const {
    control,
    register,
    watch,
    handleSubmit,
    formState: { errors, dirtyFields, isSubmitting },
  } = useForm<AttributeForm>({ resolver: classValidatorResolver(AttributeForm), values: attribute });
  const currentTypeValue = watch('type');
  const currentMinimumValue = watch('minimumValue');
  const currentMaximumValue = watch('maximumValue');
  const currentNumberOfDecimals = watch('numberOfDecimals');
  const currentValuesValue = watch('values');

  const onSubmit: SubmitHandler<AttributeForm> = async (submittedValues) => {
    if (!attribute) {
      const { domains, defaultValue, ...values } = submittedValues;
      await createAttribute({
        ...values,
        defaultValue: defaultValue ? `${defaultValue}` : null,
        nodeId: +nodeId!,
        domainIds: domains.map(({ id }) => id),
      });
    } else {
      const { domains, defaultValue, ...values } = getDirtyValues(dirtyFields, submittedValues);
      const domainsToAdd = domains?.filter(({ id }) => !attribute.domains.find((domain) => domain.id === id)) ?? [];
      const domainsToRemove = domains
        ? attribute.domains.filter(({ id }) => !domains?.find((domain) => domain.id === id))
        : [];

      await Promise.all([
        updateAttribute({
          ...values,
          defaultValue: defaultValue || typeof defaultValue === 'number' ? `${defaultValue}` : defaultValue,
          nodeId: +nodeId!,
          attributeId: attribute.id,
        }),
        ...domainsToAdd.map(({ id: domainId }) =>
          assignDomainToAttribute({ nodeId: +nodeId!, attributeId: attribute.id, domainId }),
        ),
        ...domainsToRemove.map(({ id: domainId }) =>
          removeDomainFromAttribute({ nodeId: +nodeId!, attributeId: attribute.id, domainId }),
        ),
      ]);
    }

    navigate('..');
  };

  const renderContent = () => {
    if (gettingError || domainsError) {
      return <Alert severity="error">{(gettingError || domainsError)?.response?.data?.message}</Alert>;
    }

    return (
      <>
        <TextField
          {...register('name')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={creatingAttribute || updatingAttribute}
          error={Boolean(errors.name)}
          helperText={errors.name?.message}
          label="Name"
          margin="dense"
          size="small"
          fullWidth
        />
        <TextField
          {...register('description', { setValueAs: (value) => value || null })}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={creatingAttribute || updatingAttribute}
          error={Boolean(errors.description)}
          helperText={errors.description?.message}
          label="Description"
          margin="dense"
          size="small"
          fullWidth
        />
        <Controller
          control={control}
          name="type"
          render={({ field }) => (
            <Autocomplete
              {...register('type')}
              loading={loadingAttribute}
              sx={{ mt: '1rem' }}
              onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
              onBlur={field.onBlur}
              value={field.value}
              options={Object.values(AttributeType)}
              renderInput={(params) => (
                <TextField {...params} label="Type" error={Boolean(errors.type)} helperText={errors.type?.message} />
              )}
            />
          )}
        />
        <Controller
          control={control}
          name="entryType"
          render={({ field }) => (
            <Autocomplete
              {...register('entryType')}
              loading={loadingAttribute}
              sx={{ mt: '1rem' }}
              onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
              onBlur={field.onBlur}
              value={field.value}
              options={Object.values(AttributeEntryType)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Entry Type"
                  error={Boolean(errors.entryType)}
                  helperText={errors.entryType?.message}
                />
              )}
            />
          )}
        />
        {currentTypeValue === AttributeType.STRING ? (
          <TextField
            {...register('maximumValue', { setValueAs: (value) => Number(value) || null })}
            type="number"
            sx={{ mt: '1rem' }}
            variant="outlined"
            slotProps={{ htmlInput: { min: 0 } }}
            disabled={creatingAttribute || updatingAttribute}
            error={Boolean(errors.maximumValue)}
            helperText={errors.maximumValue?.message}
            label="Maximum length"
            margin="dense"
            size="small"
            fullWidth
          />
        ) : null}
        {currentTypeValue === AttributeType.INTEGER || currentTypeValue === AttributeType.DOUBLE ? (
          <>
            <Box sx={{ mt: '1rem', display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
              <TextField
                {...register('minimumValue', { setValueAs: (value) => Number(value) || null })}
                type="number"
                variant="outlined"
                slotProps={{
                  htmlInput: {
                    max: currentMaximumValue,
                    step:
                      currentTypeValue === AttributeType.DOUBLE
                        ? `0.${'0'.repeat((currentNumberOfDecimals || 1) - 1)}1`
                        : '1',
                  },
                }}
                disabled={creatingAttribute || updatingAttribute}
                error={Boolean(errors.minimumValue)}
                helperText={errors.minimumValue?.message}
                label="Minimum value"
                margin="dense"
                size="small"
              />
              <TextField
                {...register('defaultValue', { setValueAs: (value) => Number(value) || null })}
                type="number"
                variant="outlined"
                slotProps={{
                  htmlInput: {
                    min: currentMinimumValue,
                    max: currentMaximumValue,
                    step:
                      currentTypeValue === AttributeType.DOUBLE
                        ? `0.${'0'.repeat((currentNumberOfDecimals || 1) - 1)}1`
                        : '1',
                  },
                }}
                disabled={creatingAttribute || updatingAttribute}
                error={Boolean(errors.defaultValue)}
                helperText={errors.defaultValue?.message}
                label="Default value"
                margin="dense"
                size="small"
              />
              <TextField
                {...register('maximumValue', { setValueAs: (value) => Number(value) || null })}
                type="number"
                variant="outlined"
                slotProps={{
                  htmlInput: {
                    min: currentMinimumValue,
                    step:
                      currentTypeValue === AttributeType.DOUBLE
                        ? `0.${'0'.repeat((currentNumberOfDecimals || 1) - 1)}1`
                        : '1',
                  },
                }}
                disabled={creatingAttribute || updatingAttribute}
                error={Boolean(errors.maximumValue)}
                helperText={errors.maximumValue?.message}
                label="Maximum value"
                margin="dense"
                size="small"
              />
            </Box>
          </>
        ) : null}
        {currentTypeValue === AttributeType.DOUBLE ? (
          <TextField
            {...register('numberOfDecimals', { valueAsNumber: true })}
            type="number"
            variant="outlined"
            slotProps={{ htmlInput: { min: 0 } }}
            sx={{ mt: '1rem' }}
            disabled={creatingAttribute || updatingAttribute}
            error={Boolean(errors.numberOfDecimals)}
            helperText={errors.numberOfDecimals?.message}
            label="Number of decimals"
            margin="dense"
            size="small"
            fullWidth
          />
        ) : null}
        {currentTypeValue === AttributeType.LIST ? (
          <Controller
            control={control}
            name="values"
            render={({ field }) => (
              <Autocomplete
                {...register('values', { setValueAs: (value) => value || null })}
                loading={loadingAttribute}
                sx={{ mt: '1rem' }}
                multiple
                freeSolo
                onChange={(_event, selectedOptions) =>
                  field.onChange([...new Set(selectedOptions.flatMap((x) => x.split(',')))])
                }
                onBlur={field.onBlur}
                value={field.value ?? []}
                options={field.value ?? []}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Values"
                    error={Boolean(errors.values)}
                    helperText={errors.values?.message}
                  />
                )}
              />
            )}
          />
        ) : null}
        {currentTypeValue === AttributeType.LIST ? (
          <Controller
            control={control}
            name="defaultValue"
            render={({ field }) => (
              <Autocomplete
                {...register('defaultValue', { setValueAs: (value) => value || null })}
                sx={{ mt: '1rem' }}
                onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
                onBlur={field.onBlur}
                value={field.value}
                options={currentValuesValue ?? []}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Default value"
                    error={Boolean(errors.defaultValue)}
                    helperText={errors.defaultValue?.message}
                  />
                )}
              />
            )}
          />
        ) : null}
        {currentTypeValue !== AttributeType.INTEGER &&
        currentTypeValue !== AttributeType.DOUBLE &&
        currentTypeValue !== AttributeType.LIST ? (
          <TextField
            {...register('defaultValue', { setValueAs: (value) => value || null })}
            variant="outlined"
            sx={{ mt: '1rem' }}
            disabled={creatingAttribute || updatingAttribute}
            error={Boolean(errors.defaultValue)}
            helperText={errors.defaultValue?.message}
            label="Default value"
            margin="dense"
            size="small"
            fullWidth
          />
        ) : null}
        <Controller
          control={control}
          name="domains"
          render={({ field }) => (
            <Autocomplete
              {...register('domains', { setValueAs: (value) => value || [] })}
              loading={loadingDomains}
              sx={{ mt: '1rem' }}
              multiple
              onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
              onBlur={field.onBlur}
              value={field.value}
              getOptionLabel={(domain) => domain?.shortName ?? ''}
              options={domainsResponse?.pages.map(({ result }) => result).flat() ?? []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Domains"
                  error={Boolean(errors.domains)}
                  helperText={errors.domains?.message}
                />
              )}
            />
          )}
        />
      </>
    );
  };

  if (loadingAttribute) {
    // FIXME: workaround for react-use-form not resetting controlled fields in a dialog
    return <LoadingIndicator />;
  }

  return (
    <Dialog open={true}>
      {loadingAttribute ? (
        <LoadingIndicator />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>{`${attribute ? 'Edit' : 'Add'} Attribute`}</DialogTitle>
          <DialogContent>
            {renderContent()}
            {createError || updateError ? (
              <Alert sx={{ mt: '1rem' }} severity="error">
                {createError?.response?.data?.message || updateError?.response?.data?.message}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
              Cancel
            </Button>
            <LoadingButton
              color="primary"
              type="submit"
              variant="contained"
              loading={isSubmitting || Boolean(gettingError)}
            >
              {attribute ? 'Save' : 'Create'}
            </LoadingButton>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};
